import { useRef, useEffect } from 'react';

import { IconComponent } from '../../../../util/icons';
import vendorFeedPhoto from '../../../../asset/images/buildium/Buildium-feed.png';
import vendorSyncProperty from '../../../../asset/images/buildium/Buidlium-sync.png';
import buildiumStep2 from '../../../../asset/images/buildium/Buildium-Step2.png';
import buildiumStep3 from '../../../../asset/images/buildium/Buildium-Step3.png';
import buildiumStep4 from '../../../../asset/images/buildium/Buildium-Step4.png';
import buildiumStep5 from '../../../../asset/images/buildium/Buildium-Step5.png';
import buildiumStep6 from '../../../../asset/images/buildium/Buildium-Step6.png';
import buildiumStep7 from '../../../../asset/images/buildium/Buildium-Step7.png';
import buildiumStep8 from '../../../../asset/images/buildium/Buildium-Step8.png';
import buildiumStep9 from '../../../../asset/images/buildium/Buildium-Step9.png';
import buildiumStep10 from '../../../../asset/images/buildium/Buildium-Step10.png';

import { Typography } from '@/components/ui/typography';

interface StepData {
  step: number;
  description: React.ReactNode;
  image?: string;
  imageAlt?: string;
  extra?: React.ReactNode;
}

const STEPS: StepData[] = [
  { step: 1, description: 'Click "Manage integrations"', image: buildiumStep2, imageAlt: 'Manage Integrations' },
  {
    step: 2,
    description: <><Typography variant="heading-md" className="font-normal">Click "</Typography><Typography variant="heading-md" className="font-bold">Enable</Typography><Typography variant="heading-md" className="font-normal">"</Typography></>,
    image: buildiumStep3, imageAlt: 'Enable',
  },
  {
    step: 3,
    description: 'Click "Connect with Buildium" (a new Buildium tab will appear)',
    image: buildiumStep4, imageAlt: 'User Profile',
  },
  {
    step: 4,
    description: <><Typography variant="heading-md" className="font-normal">Sign in with your </Typography><Typography variant="heading-md" className="font-bold">Buildium </Typography><Typography variant="heading-md" className="font-normal">credentials</Typography></>,
    image: buildiumStep5, imageAlt: 'User Profile',
  },
  { step: 5, description: 'Check the "Terms and conditions" checkbox', image: buildiumStep6, imageAlt: 'Settings' },
  { step: 6, description: 'Click "Allow access"', image: buildiumStep7, imageAlt: 'Users Roles and API' },
  {
    step: 7,
    description: <><Typography variant="heading-md" className="font-bold">PlanOmatic  </Typography><Typography variant="heading-md" className="font-normal">{'tab automatically opens showing an "Active"'}</Typography><Typography variant="heading-md" className="font-bold"> Buildium </Typography><Typography variant="heading-md" className="font-normal">integration</Typography></>,
    image: buildiumStep8, imageAlt: 'API Tab',
  },
  {
    step: 8,
    description: 'Manage the Integration Configuration',
    image: buildiumStep9, imageAlt: 'New API Key',
    extra: (
      <div className="ml-6 mt-1">
        <Typography variant="heading-md" className="font-normal block">Auto-Sync Assets (ON by default)</Typography>
        <Typography variant="heading-md" className="font-normal block">Watermarked Photos (OFF by default)</Typography>
      </div>
    ),
  },
  { step: 9, description: 'Click "Save & sync properties"', image: buildiumStep10, imageAlt: 'Name Field' },
  {
    step: 10,
    description: <Typography variant="heading-md" className="font-bold">Watch your properties magically appear!</Typography>,
  },
];

const renderStepDescription = (step: StepData) => {
  if (typeof step.description === 'string') {
    return (
      <>
        <Typography variant="heading-md" className="font-bold">Step {step.step}: </Typography>
        <Typography variant="heading-md" className="font-normal">{step.description}</Typography>
      </>
    );
  }
  return (
    <>
      <Typography variant="heading-md" className="font-bold">Step {step.step}: </Typography>
      {step.description}
    </>
  );
};

export const IntegrationOverviewBuildium = (props: {
  isHowToConfigLinkClicked: boolean;
}) => {
  const howToConfigureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (props.isHowToConfigLinkClicked) {
      howToConfigureRef?.current?.scrollIntoView();
    }
  }, [props.isHowToConfigLinkClicked]);

  return (
    <>
      <div className="flex flex-col gap-6 py-6">
        <div className="flex items-center gap-6 p-4">
          <div className="flex flex-col gap-3 flex-1">
            <Typography variant="heading-lg" className="font-medium">Sync your property data to PlanOmatic.</Typography>
            <Typography className="text-lg font-medium text-muted-foreground">Track which vacancies already have marketing photos, and place photo orders directly from the PlanOmatic platform with one click.</Typography>
          </div>
          <div>
            <IconComponent src={vendorSyncProperty} width={450} height={390} alt="Vendor Feed" />
          </div>
        </div>
        <div className="flex items-center gap-6 p-4">
          <div>
            <IconComponent src={vendorFeedPhoto} width={450} height={390} alt="Vendor Sync Property" />
          </div>
          <div className="ml-auto flex flex-col gap-3">
            <Typography variant="heading-lg" className="font-medium">Feed your listing media to Buildium automatically.</Typography>
            <Typography className="text-lg font-medium text-muted-foreground">As soon as the order is ready, the assets are automatically delivered to Buildium!</Typography>
          </div>
        </div>
      </div>

      <div ref={howToConfigureRef} className="flex flex-col gap-4 py-6">
        <Typography variant="heading-2xl" className="font-semibold">Instructions to Enable Integration</Typography>
        <Typography className="text-lg font-semibold">Follow the step-by-step guide below to enable your PlanOmatic & Buildium integration</Typography>
        {STEPS.map((step) => (
          <div key={step.step}>
            <div className="flex items-center gap-1 flex-wrap">{renderStepDescription(step)}</div>
            {step.extra}
            {step.image && (
              <div className="mt-3 border rounded-md overflow-hidden inline-block">
                <IconComponent src={step.image} width={700} height={393} alt={step.imageAlt ?? ''} />
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};
